import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
// Direct file import, not the `shared/components/common` barrel — the barrel pulls
// in auth-context consumers that this unauthenticated wizard step must not require.
import { CollapsibleSection } from '../../../shared/components/common/CollapsibleSection';
import { ModuleMaturityBadge } from '../../../shared/components/ModuleMaturityBadge';
import { getIcon } from '../../../shared/config/iconMap';
import { getModuleById } from '../../../shared/config/modules.config';
import type { SetupModule } from '../../../shared/api/setupWizardApi';
import { WIZARD_VISIBILITY_MODES, type WizardVisibilityMode } from './options';

interface ModulesStepProps {
  modules: SetupModule[];
  selectedModules: string[];
  recommendedModules: string[];
  hasRoleInput: boolean;
  onChange: (modules: string[]) => void;
  /** Per-object_type visibility mode chosen for configurable selected modules. */
  visibilityModes: Record<string, string>;
  onVisibilityChange: (objectType: string, mode: string) => void;
  /**
   * Mode applied to any module the admin hasn't answered — derived from the
   * company size band on Step 1. Rendering it as the selected radio keeps the UI
   * honest: what's checked here is exactly what the backend will seed.
   */
  defaultVisibilityMode: WizardVisibilityMode;
}

export function ModulesStep({
  modules,
  selectedModules,
  recommendedModules,
  hasRoleInput,
  onChange,
  visibilityModes,
  onVisibilityChange,
  defaultVisibilityMode,
}: ModulesStepProps) {
  const { t } = useTranslation(['user_management', 'common']);

  const toggleModule = (moduleId: string) => {
    onChange(
      selectedModules.includes(moduleId)
        ? selectedModules.filter((m) => m !== moduleId)
        : [...selectedModules, moduleId]
    );
  };

  // Recommended modules lead the step so a first-time admin sees the shortlist we
  // derived from their role before the full catalog. Backend order is preserved
  // within each group.
  const recommended = modules.filter((m) => recommendedModules.includes(m.id));
  const others = modules.filter((m) => !recommendedModules.includes(m.id));

  // Selected modules that expose a tenant-wide visibility mode (only these get a
  // wizard question). Config-driven via the backend `visibility_object_type`.
  const configurableSelected = modules.filter(
    (m) => m.visibility_object_type && selectedModules.includes(m.id)
  );

  // The collapsed summary must describe the modes actually chosen, not the default:
  // the step unmounts when the admin moves on, so a re-entered step re-collapses and
  // would otherwise claim the band default over a mode they had changed.
  const hasNonDefaultVisibility = configurableSelected.some(
    (m) =>
      (visibilityModes[m.visibility_object_type as string] ?? defaultVisibilityMode) !==
      defaultVisibilityMode
  );

  const renderModuleCard = (module: SetupModule) => {
    const icon = getIcon(module.icon);
    const isSelected = selectedModules.includes(module.id);
    const isRecommended = recommendedModules.includes(module.id);
    // Maturity comes from the same module config the module picker and home page
    // read, so a module's β/α marker can never drift between the two surfaces.
    const status = getModuleById(module.id)?.status;
    return (
      <div
        key={module.id}
        role="button"
        tabIndex={0}
        aria-pressed={isSelected}
        onClick={() => toggleModule(module.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleModule(module.id);
          }
        }}
        className={cn(
          'flex cursor-pointer items-start gap-3 rounded-md border p-4 text-start transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
        )}
      >
        <Checkbox checked={isSelected} className="pointer-events-none mt-0.5" tabIndex={-1} />
        {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">{t(module.name_key, { ns: 'common' })}</span>
            {status && <ModuleMaturityBadge status={status} />}
            {isRecommended && (
              <Badge variant="secondary" className="py-0 text-xs">
                {t('setupWizardV2.modules.recommended')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t(module.description_key, { ns: 'common' })}
          </p>
        </div>
      </div>
    );
  };

  const renderGrid = (group: SetupModule[]) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{group.map(renderModuleCard)}</div>
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {hasRoleInput
          ? t('setupWizardV2.modules.descriptionWithRecommendations')
          : t('setupWizardV2.modules.description')}
      </p>

      {recommended.length > 0 ? (
        <>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">
              {t('setupWizardV2.modules.recommendedHeading')}
            </h3>
            {renderGrid(recommended)}
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">{t('setupWizardV2.modules.moreHeading')}</h3>
            {renderGrid(others)}
          </div>
        </>
      ) : (
        renderGrid(others)
      )}

      {/* Per-module default visibility (Sharing Infra P-4). Collapsed by default —
          the untouched state already means whatever the company's size band implies
          (small companies share everything; medium-and-up start restricted), so a new
          admin never has to answer a sharing-policy question during onboarding. */}
      {configurableSelected.length > 0 && (
        <div className="border-t pt-6">
          <CollapsibleSection
            label={t('setupWizardV2.visibility.advancedHeading')}
            summary={
              hasNonDefaultVisibility
                ? t('setupWizardV2.visibility.collapsedSummaryCustomized')
                : t(`setupWizardV2.visibility.collapsedSummary.${defaultVisibilityMode}`)
            }
          >
            <p className="text-sm text-muted-foreground">
              {t('setupWizardV2.visibility.description')}
            </p>
            {configurableSelected.map((module) => {
              const objectType = module.visibility_object_type as string;
              const value = visibilityModes[objectType] ?? defaultVisibilityMode;
              return (
                <div key={module.id} className="space-y-2">
                  <span className="text-sm font-medium">{t(module.name_key, { ns: 'common' })}</span>
                  <RadioGroup
                    value={value}
                    onValueChange={(v) => onVisibilityChange(objectType, v)}
                    className="space-y-2"
                  >
                    {WIZARD_VISIBILITY_MODES.map((mode) => (
                      <div key={mode} className="flex items-start space-x-3">
                        <RadioGroupItem
                          value={mode}
                          id={`wizard_vis_${objectType}_${mode}`}
                          className="mt-1"
                        />
                        <div className="space-y-0.5">
                          <Label
                            htmlFor={`wizard_vis_${objectType}_${mode}`}
                            className="cursor-pointer"
                          >
                            {t(`common:visibilityMode.${mode}.label`)}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {t(`common:visibilityMode.${mode}.description`)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              );
            })}
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
}
